import { memo, useCallback, useMemo, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { DownOutlined } from '@ant-design/icons';
import {
    Dropdown,
    type DropdownProps,
    Flex,
    type MenuProps,
    Typography,
} from 'antd';
import cn from 'classnames';
import type { MenuInfo } from 'rc-menu/lib/interface';

import { type Language, LANGUAGE } from '@/shared/constants/language';
import { useLanguage } from '@/shared/lib/hooks';

import { LanguageSwitcherItem } from '../language-switcher-item/language-switcher-item';

import cls from './language-switcher.module.css';

interface LanguageSwitcherProps {
    className?: string;
    rootClassName?: string;
    isAuth?: boolean;
    isMenu?: boolean;
    onLanguageChange?: (language: Language) => void | Promise<void>;
}

export const LanguageSwitcher = memo((props: LanguageSwitcherProps) => {
    const {
        className,
        rootClassName,
        isAuth = false,
        isMenu = false,
        onLanguageChange,
    } = props;

    const [isOpened, setIsOpened] = useState<boolean>(false);

    const [language, changeLanguage] = useLanguage();

    const { t } = useTranslation();

    const menuItems = useMemo<Required<MenuProps>['items']>(
        () => [
            {
                key: LANGUAGE.english,
                label: (
                    <LanguageSwitcherItem
                        isAuth={isAuth}
                        isMenuItem
                        language={LANGUAGE.english}
                    />
                ),
            },
            {
                key: LANGUAGE.italian,
                label: (
                    <LanguageSwitcherItem
                        isAuth={isAuth}
                        isMenuItem
                        language={LANGUAGE.italian}
                    />
                ),
            },
        ],
        [isAuth],
    );

    const onChangeLanguageHandler = useCallback(
        async ({ key }: MenuInfo) => {
            const newLanguage = key as Language;

            changeLanguage(newLanguage);

            if (onLanguageChange) {
                await onLanguageChange(newLanguage);
            }

            if (isMobile || isTablet) {
                setIsOpened(false);
            }
        },
        [changeLanguage, onLanguageChange],
    );

    const menuSettings = useMemo<MenuProps>(
        () => ({
            items: menuItems,
            selectable: true,
            selectedKeys: [language],
            onClick: onChangeLanguageHandler,
        }),
        [language, onChangeLanguageHandler, menuItems],
    );

    const onOpenChangeHandler = useCallback<
        Required<DropdownProps>['onOpenChange']
    >((nextOpen, info) => {
        if (info.source === 'trigger' || nextOpen) {
            setIsOpened(nextOpen);
        }
    }, []);

    const content = (
        <Dropdown
            className={cn(className)}
            menu={menuSettings}
            onOpenChange={onOpenChangeHandler}
            open={isOpened}
            rootClassName={cn(cls.root, rootClassName, { [cls.auth]: isAuth })}
        >
            <Typography.Text className={cls.trigger}>
                <LanguageSwitcherItem
                    additionSlot={<DownOutlined />}
                    isAuth={isAuth}
                    language={language}
                />
            </Typography.Text>
        </Dropdown>
    );

    if (isAuth) {
        return content;
    }

    if (isMenu) {
        return (
            <Flex
                align="center"
                justify="space-between"
            >
                <Typography.Text>{t('language')}</Typography.Text>
                {content}
            </Flex>
        );
    }

    return content;
});

LanguageSwitcher.displayName = 'LanguageSwitcher';
